import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useStore } from "react-admin";
import { calcTotalPayable } from "@/utils";
import type { CurrentEvent } from "@/containers/events/types";

type MemberRow = { hasChair?: boolean; hasRelay?: boolean };

type UseNiyaazFormEffectsOptions = {
  syncTotal?: boolean;
  resetPirsaWhenDisabled?: boolean;
  setPirsaWhenEnabled?: boolean;
};

const allMembers = (familyMembers: MemberRow[] | undefined, mehmaans: MemberRow[] | undefined) => [
  ...(familyMembers ?? []),
  ...(mehmaans ?? []),
];

export const useNiyaazFormEffects = ({
  syncTotal = false,
  resetPirsaWhenDisabled = false,
  setPirsaWhenEnabled = false,
}: UseNiyaazFormEffectsOptions = {}) => {
  const { setValue, getValues } = useFormContext();
  const [currentEvent] = useStore<CurrentEvent | null>("currentEvent");

  const takhmeenAmount = useWatch({ name: "takhmeenAmount" });
  const iftaari = useWatch({ name: "iftaari" });
  const chairs = useWatch({ name: "chairs" });
  const zabihat = useWatch({ name: "zabihat" });
  const relayRequired = useWatch({ name: "relayRequired" });
  const pirsa = useWatch({ name: "pirsa" });
  const pirsaRequired = useWatch({ name: "pirsaRequired" });
  const familyMembers = useWatch({ name: "familyMembers" }) as MemberRow[] | undefined;
  const mehmaans = useWatch({ name: "mehmaans" }) as MemberRow[] | undefined;
  const mehmaanCount = useWatch({ name: "mehmaanCount" });

  const payable = calcTotalPayable(currentEvent ?? {}, {
    takhmeenAmount,
    iftaari,
    chairs,
    mehmaanCount,
    zabihat,
    relayRequired,
    pirsa,
  });

  useEffect(() => {
    const chairCount = allMembers(familyMembers, mehmaans).filter(
      (member) => member.hasChair
    ).length;
    setValue("chairs", chairCount, { shouldDirty: false });
  }, [familyMembers, mehmaans, setValue]);

  useEffect(() => {
    setValue("mehmaanCount", mehmaans?.length || 0, { shouldDirty: false });
  }, [mehmaans, setValue]);

  useEffect(() => {
    const family = familyMembers ?? [];
    const mehmaanList = mehmaans ?? [];
    const members = allMembers(familyMembers, mehmaans);
    const storedRelayRequired = Boolean(getValues("relayRequired"));
    const anyMemberRelay = members.some((member) => member.hasRelay === true);

    // Legacy rows: relayRequired on the form but no per-member hasRelay in JSON yet.
    const isLegacyRelayMemberData = members.every((member) => member.hasRelay === undefined);

    if (storedRelayRequired && !anyMemberRelay && isLegacyRelayMemberData) {
      if (family.length > 0) {
        setValue("familyMembers.0.hasRelay", true, { shouldDirty: false });
        setValue("relayRequired", true, { shouldDirty: false });
        return;
      }
      if (mehmaanList.length > 0) {
        setValue("mehmaans.0.hasRelay", true, { shouldDirty: false });
        setValue("relayRequired", true, { shouldDirty: false });
        return;
      }
    }

    setValue("relayRequired", anyMemberRelay, { shouldDirty: false });
  }, [familyMembers, mehmaans, getValues, setValue]);

  useEffect(() => {
    if (setPirsaWhenEnabled) {
      setValue("pirsa", pirsaRequired ? 1 : 0, { shouldDirty: false });
    }
  }, [pirsaRequired, setPirsaWhenEnabled, setValue]);

  useEffect(() => {
    if (resetPirsaWhenDisabled && !pirsaRequired) {
      setValue("pirsa", 0, { shouldDirty: false });
      setValue("pirsaSelfPickup", false, { shouldDirty: false });
    }
  }, [pirsaRequired, resetPirsaWhenDisabled, setValue]);

  useEffect(() => {
    if (syncTotal) {
      setValue("total", payable, { shouldDirty: false });
    }
  }, [payable, syncTotal, setValue]);

  return {
    currentEvent,
    payable,
  };
};
